/*
 * DCD CV-07 (2026-11-03) "Finish your run": matching trim of the same finish on cabinet pages and in the cart.
 * Config comes from snippets/dcd-finish-run.liquid (data-cfg). For each slot (toe kick, base filler, wall filler,
 * end panel, crown) we try the brand's candidate handle suffixes in order and keep the first product that exists,
 * is a Panel, and is available. Nothing is shown for a slot that has no match, so there are no dead links.
 * Handle suffixes were read from the live catalog on 11/3 (data/fixes_1103/cv07/work/trim_*.json, coverage.txt).
 */
const H_DEFAULT = [42, 36, 30];
const uniq = (a) => [...new Set(a)];

function wallHeight(title) {
  // 15" W x 42" H, 15"W x 30"H, 30" W X 36" H
  const m = /(\d+(?:\.\d+)?)(?:\s+\d\/\d)?"\s*H\b/i.exec(title || '');
  return m ? Math.round(parseFloat(m[1])) : 0;
}
function hList(h, allowed) {
  const out = [];
  if (allowed.includes(h)) out.push(h);
  for (const d of H_DEFAULT) if (allowed.includes(d)) out.push(d);
  return uniq(out);
}

/* Per brand: slot -> function(ctx) returning candidate suffixes. ctx = {k: kind, h: wall height, pat, name, t: title}
 * Slots: bf base filler, toe toe kick, end end panel (for ctx.k), dw dishwasher end panel, trim1 scribe molding,
 * trim2 shoe / quarter round / base board molding, wf wall filler, crown, lr light rail, tf tall filler. */
const FRAMELESS = /^fabuwood-(ovela|illume)-/;
const OVELA_SIENA = /^fabuwood-ovela-siena-/;
const RULES = {
  Fabuwood: {
    // Ovela and Illume (frameless) carry the 3/4" toe kick; the framed lines the 1/4" one. Siena handles end in the SKU code.
    toe: (c) =>
      FRAMELESS.test(c.pat)
        ? ['96-w-x-4-1-2-h-x-3-4-d-toe-kick', '96-w-x-4-1-2-h-x-3-4-d-toe-kick-tk8-3-4', '96-w-x-4-1-2-h-x-1-4-d-toe-kick', '96-w-x-4-1-2-h-1-4-d-toe-kick']
        : ['96-w-x-4-1-2-h-x-1-4-d-toe-kick', '96-w-x-4-12-h-x-14-d-toe-kick', '96-w-x-4-1-2-h-x-3-4-d-toe-kick', '96-w-x-4-12-h-x-34-d-toe-kick'],
    bf: (c) =>
      OVELA_SIENA.test(c.pat)
        ? ['3-w-x-30-h-x-3-4-d-base-filler-3-bf3', '3-w-x-30-h-x-3-4-d-base-filler-3']
        : /^fabuwood-ovela-/.test(c.pat)
          ? ['3-w-x-34-1-2-h-base-filler', '3-w-x-30-h-base-filler']
          : ['3-w-x-30-h-base-filler', '3-w-x-30-h-base-filler-bf3', '3-w-x-34-1-2-h-base-filler'],
    dw: (c) =>
      FRAMELESS.test(c.pat)
        ? ['3-w-x-34-1-2-h-x-24-d-dishwasher-end', '3-w-x-34-1-2-h-x-24-d-dishwasher-end-dwr3']
        : ['3-w-x-34-1-2-h-x-24-d-dishwasher-end-panel', '3-w-x-34-12-h-x-24-d-dishwasher-end-panel'],
    trim1: (c) => (FRAMELESS.test(c.pat) ? [] : ['96-w-x-3-4-h-x-1-4-d-simple-scribe-molding', '96-w-x-34-h-x-14-d-simple-scribe-molding']),
    trim2: (c) =>
      OVELA_SIENA.test(c.pat)
        ? ['5-1-4-w-x-96-h-x-3-4-d-base-board-molding-5-1-4-x-96-bbm', '5-1-4-w-x-96-h-x-3-4-d-base-board-molding-5-1-4-x-96']
        : FRAMELESS.test(c.pat)
          ? ['96-w-x-4-1-2-h-x-3-4-d-base-board-molding', '96-w-x-4-1-2-h-x-3-4-d-base-board-molding-bbm']
          : ['96-w-x-3-4-h-x-3-4-d-quarter-round-molding', '96-w-x-34-h-x-34-d-quarter-round-molding'],
    wf: (c) => hList(c.h, [30, 36, 39, 42]).slice(0, 2).flatMap((h) => [`3-w-x-${h}-h-wall-filler`, `3-w-x-${h}-h-wall-filler-wf3${h}`]),
    // Corner wall filler (CF330 / CF336 / CF342): Ovela and Illume only (frameless).
    cf: (c) => (FRAMELESS.test(c.pat) ? hList(c.h, [30, 36, 42]).slice(0, 1).flatMap((h) => [`3-w-x-${h}-h-corner-wall-filler-cf3${h}`, `3-w-x-${h}-h-corner-wall-filler`]) : []),
    // Ovela and Illume carry no crown in the catalog (11/3), so skip the lookups there.
    crown: (c) =>
      FRAMELESS.test(c.pat)
        ? []
        : (/-(luna|nexus|echo)-/.test(c.pat) ? ['96-w-crown-molding-1'] : []).concat([
            '96-w-x-3-1-4-h-x-3-1-4-d-shaker-crown-molding', '96-w-x-3-14-h-x-3-14-d-shaker-crown-molding',
            '96-w-crown-molding-1', '96-w-x-2-1-2-h-x-2-3-8-d-small-crown-molding',
          ]),
    lr: (c) =>
      OVELA_SIENA.test(c.pat)
        ? ['1-1-2-w-x-96-h-x-1-d-light-rail-molding-96-lrm', '1-1-2-w-x-96-h-x-1-d-light-rail-molding-96']
        : FRAMELESS.test(c.pat)
          ? ['96-w-x-1-1-2-h-x-3-4-d-light-rail-molding', '96-w-x-1-1-2-h-x-3-4-d-light-rail-molding-lrm', '96-w-x-1-1-2-h-x-2-d-light-rail-and-starter-molding']
          : ['96-w-x-1-h-x-1-1-2-d-light-rail-molding', '96-w-x-1-h-x-1-12-d-light-rail-molding'],
    // Siena has no TF396; its 96" filler is the 2 1/2" x 96" OLF396 (owner 11/3: tall filler or 96" filler).
    tf: (c) => (FRAMELESS.test(c.pat) ? ['3-w-x-96-h-tall-filler', '2-1-2-w-x-96-h-x-3-4-d-overlay-filler-396-olf396', '2-1-2-w-x-96-h-x-3-4-d-overlay-filler-396'] : ['3-w-x-96-h-tall-filler']),
    // Allure, Ovela (Catalina, Tuscany), Quest and Value sides are a Finished End option on the cabinet, so base and wall pages get
    // no panel there; tall cabinets get the fridge / pantry panel. Illume boxes never match the door, so Illume gets panels
    // everywhere. Ovela Siena shows its own panels (owner 11/3: "all its trim"). Vanities: the vanity (or base) end panel
    // wherever the finish has one (owner 11/3).
    end: (c) => {
      const illume = /^fabuwood-illume-/.test(c.pat);
      const siena = OVELA_SIENA.test(c.pat);
      if (c.k === 'Tall') {
        if (/^fabuwood-ovela-/.test(c.pat)) return ['3-4-w-x-96-h-x-24-7-8-d-tall-end-panel', '25-w-x-96-h-tall-end-panel-3-4-tep', '25-w-x-96-h-tall-end-panel-3-4', '25-w-x-96-h-refrigerator-end-panel-3-4-x-96-x-25-deep-rep2596'];
        if (illume) return ['3-4-w-x-96-h-x-24-3-4-d-end-panel', '3-4-w-x-96-h-x-30-d-end-panel', '3-4-w-x-96-h-x-30-d-end-panel-rep3096'];
        return ['3-4-w-x-96-h-x-24-d-end-panel', '34-w-x-96-h-x-24-d-end-panel'];
      }
      if (c.k === 'Vanity') {
        if (siena) return ['3-4-w-x-34-1-2-h-x-22-d-vanity-base-end-panel-3-4-vbep', '3-4-w-x-34-1-2-h-x-22-d-vanity-base-end-panel-3-4', '24-w-x-34-1-2-h-x-3-4-d-base-end-panel-3-4-bep'];
        if (illume) return ['3-4-w-x-34-1-2-h-x-21-3-4-d-vanity-base-end-panel', '3-4-w-x-34-1-2-h-x-24-3-4-d-base-end-panel-bep', '3-4-w-x-34-1-2-h-x-24-3-4-d-base-end-panel'];
        if (/^fabuwood-ovela-/.test(c.pat)) return ['3-4-w-x-34-1-2-h-x-21-7-8-d-vanity-base-end-panel', '3-4-w-x-34-1-2-h-x-24-7-8-d-base-end-panel'];
        return ['3-4-w-x-34-1-2-h-x-24-d-base-end-panel', '34-w-x-34-12-h-x-24-d-base-end-panel'];
      }
      if (siena) {
        if (c.k === 'Base') return ['24-w-x-34-1-2-h-x-3-4-d-base-end-panel-3-4-bep', '24-w-x-34-1-2-h-x-3-4-d-base-end-panel-3-4'];
        if (c.k === 'Wall') return hList(c.h, [30, 36, 42]).slice(0, 1).flatMap((h) => [`3-w-x-${h}-h-x-13-7-8-d-wall-end-panel-wep14${h}-x-3`, `3-w-x-${h}-h-x-13-7-8-d-wall-end-panel`]);
      }
      if (!illume) return [];
      if (c.k === 'Base') return ['3-4-w-x-34-1-2-h-x-24-3-4-d-base-end-panel', '3-4-w-x-34-1-2-h-x-24-d-base-end-panel', '3-4-w-x-34-1-2-h-x-24-3-4-d-base-end-panel-bep'];
      if (c.k === 'Wall') return hList(c.h, [30, 36, 39, 42]).slice(0, 1).flatMap((h) => [`3-w-x-${h}-h-x-13-7-8-d-wall-end-panel`, `11-1-4-w-x-${h}-1-8-h-x-1-4-d-finished-end-skin`]);
      return [];
    },
  },
  Jarlin: {
    toe: () => ['tk8'],
    bf: () => ['bf3'],
    dw: () => ['dwp'],
    trim1: () => ['sm8', 'sm8-b'],
    trim2: () => ['qrm8', 'bm8'],
    wf: (c) => hList(c.h, [30, 36, 42]).slice(0, 2).map((h) => `wf3${h}`),
    crown: () => ['cm8-s', 'cm8', 'acm8-s', 'acm8', 'ccm8', 'cm8-c'],
    lr: () => ['tlr8-2', 'tlr8-1'],
    tf: () => ['wf396'],
    // Jarlin boxes have finished sides; the fridge / pantry panel (FP2496, per DESIGN-STYLE) is the one to suggest.
    // BP4896 (local pickup only) is never suggested.
    end: (c) => (c.k === 'Tall' ? ['fp2496', 'bp3696'] : []),
  },
  KCD: {
    toe: () => ['toe-kick'],
    bf: () => ['filler-3-x-42', 'filler-3-w-x-42-h'],
    dw: () => ['dish-washer-end-panel'],
    trim1: () => ['scribe'],
    trim2: () => ['shoe-molding'],
    wf: () => ['filler-3-x-42', 'filler-3-w-x-42-h'],
    crown: () => ['shaker-crown-molding', 'traditional-crown-molding', 'cove-crown-molding'],
    lr: () => ['light-rail-molding-transitional', 'bottom-edge-molding-narrow-light-rail', 'traditional-light-rail'],
    tf: () => ['filler-3-w-x-96-h'],
    // Base: a base-size panel (the dishwasher end panel) before the 48" x 96" back panel (owner 11/3).
    end: (c) =>
      c.k === 'Tall'
        ? ['tall-end-panel', 'back-panel-long-grain-48-w-x-96-h']
        : c.k === 'Base'
          ? ['dish-washer-end-panel', 'back-panel-long-grain-48-w-x-96-h']
          : ['back-panel-long-grain-48-w-x-96-h'],
  },
  'CNC Cabinetry': {
    // Classic (Broadway, Milano, Highland, Versailles, Woodbourne) uses TKS.25 / TKS.375 and the BFP finished end;
    // Concord, Luxor and Bedford use TKS and BEP (catalog 11/3).
    toe: (c) =>
      /^cnc-classic-/.test(c.pat)
        ? ['96-w-x-4-1-2-h-x-1-4-d-toe-kick-tks-25', '96-w-x-4-1-2-h-x-3-8-d-toe-kick-tks-375', '96-w-x-4-1-2-h-x-3-4-d-toe-kick-tks-75']
        : ['96-w-x-4-1-2-h-toe-kick-tks', '96-w-x-4-1-2-h-x-1-4-d-toe-kick-tks-25', '96-w-x-4-1-2-h-x-3-4-d-toe-kick-tks-75'],
    bf: () => ['3-w-x-42-h-fillers-f342'],
    dw: (c) =>
      /^cnc-classic-/.test(c.pat)
        ? ['3-w-x-34-1-2-h-refrigerator-dishwasher-end-panel-dwr3', '3-w-x-34-1-2-h-x-24-d-base-end-panel-dwr3']
        : ['3-w-x-34-1-2-h-x-24-d-base-end-panel-dwr3', '3-w-x-34-1-2-h-refrigerator-dishwasher-end-panel-dwr3'],
    trim1: () => ['96-w-x-1-4-h-scribe-molding-scm'],
    trim2: () => ['96-w-x-3-4-h-shoe-molding-shm'],
    wf: (c) => (c.h === 30 ? ['3-w-x-30-h-fillers-f330', '3-w-x-42-h-fillers-f342'] : ['3-w-x-42-h-fillers-f342', '3-w-x-30-h-fillers-f330']),
    crown: (c) =>
      /^cnc-classic-/.test(c.pat)
        ? ['96-w-x-3-1-2-h-x-2-1-2-d-full-overlay-crown-moldings-mflocr', '3-w-x-3-4-h-contemporary-crown-molding-ccm', '96-w-x-3-1-2-h-x-2-1-2-d-royal-crown-molding-rcrm']
        : ['96-w-x-2-h-x-2-d-shaker-crown-molding-crms', '96-w-x-2-h-x-2-d-crown-molding-crm', '96-w-x-3-1-2-h-x-2-1-2-d-full-overlay-crown-moldings-mflocr', '96-w-x-3-1-2-h-x-2-1-2-d-royal-crown-molding-rcrm'],
    lr: () => ['96-w-x-3-4-h-light-rail-molding-ltrm2', '96-w-x-1-1-8-h-light-rail-molding-ltrm1', '96-w-x-1-1-4-h-light-rail-molding-ltrm3'],
    tf: () => ['3-w-x-96-h-fillers-f396'],
    // Same panels as the CV-06 buy-box link (quote rule: WEP / BEP / UREP).
    end: (c) => {
      if (c.k === 'Wall') {
        const h = c.h > 36 ? 42 : c.h > 30 ? 36 : 30;
        const fs = h === 30 ? 30 : 42;
        return [`3-4-w-x-${h}-h-x-13-d-end-panel-wep${h}`, `1-4-w-x-${fs}-h-finished-skin-fs${fs}`];
      }
      if (c.k === 'Tall') return ['3-4-w-x-96-h-x-25-d-end-panel-urep25', '3-4-w-x-96-h-x-24-d-refrigerator-end-panel-urep24', '1-4-w-x-96-h-finished-skin-fs9624'];
      if (c.k === 'Vanity') return ['vanity-finish-end-vfp', '1-4-w-x-34-1-2-h-x-20-1-4-d-finished-skin-fsv', '3-4-w-x-34-1-2-h-x-24-d-base-end-panel-bep', 'base-finished-end-bfp'];
      if (/^cnc-classic-/.test(c.pat)) return ['base-finished-end-bfp', '3-4-w-x-34-1-2-h-x-24-d-base-end-panel-bep'];
      return ['3-4-w-x-34-1-2-h-x-24-d-base-end-panel-bep', 'base-finished-end-bfp'];
    },
  },
  Eurocraft: {
    toe: () => ['tk96b', 'tk96'],
    // Eurocraft has no separate base filler: the 3" x 36" WF336 is cut to 34 1/2" for base runs (owner 11/3).
    bf: () => ['wf336'],
    dw: () => [],
    trim1: () => ['sm8'],
    trim2: () => [],
    wf: (c) => hList(c.h, [30, 36, 42]).slice(0, 2).map((h) => `wf3${h}`),
    // Only the Eurocraft shaker finishes have crown molding (11/3).
    crown: (c) => (/shaker/i.test(c.name || '') ? ['cm8-s', 'cm8-c'] : []),
    lr: () => [],
    tf: () => ['wf396'],
    // Same panels as the CV-06 buy-box link.
    end: (c) => {
      if (c.k === 'Wall') return /24"\s*D/i.test(c.t) ? ['pnl2535'] : [`wdd13${c.h > 36 ? 42 : c.h > 30 ? 36 : 30}`];
      if (c.k === 'Tall') return ['pnl2596'];
      if (c.k === 'Vanity') return /Hanging|Floating/i.test(c.t) ? ['vhdd2224', 'vdd2235'] : ['vdd2235'];
      return ['pnl2535'];
    },
  },
};

/* What each cabinet type shows (owner 11/3): base pages = base-run trim only, wall pages = wall-run trim only, etc. */
const ORDER = {
  Base: ['bf', 'toe', 'end', 'dw', 'trim1', 'trim2'],
  Wall: ['wf', 'cf', 'crown', 'lr', 'end'],
  Tall: ['tf', 'toe', 'crown', 'end'],
  Vanity: ['toe', 'bf', 'end'],
};
const MAX_CARDS = { product: 5, cart: 10 };

function labelFor(slot, k, v, title) {
  const t = String(title || '');
  if (/dish ?washer/i.test(t)) return 'Dishwasher end panel';
  switch (slot) {
    case 'toe':
      return 'Toe kick';
    case 'bf':
    case 'wf':
      if (v === 'KCD' || v === 'CNC Cabinetry') return '3" filler';
      return slot === 'bf' ? '3" base filler' : '3" wall filler';
    case 'tf':
      return 'Tall filler';
    case 'cf':
      return 'Corner wall filler';
    case 'crown':
      return 'Crown molding';
    case 'lr':
      return 'Light rail molding';
    case 'end':
      if (k === 'Vanity' && /base end panel/i.test(t) && !/vanity/i.test(t)) return 'Base end panel';
      return `${k} end panel`;
    default:
      if (/shoe/i.test(t)) return 'Shoe molding';
      if (/quarter/i.test(t)) return 'Quarter round';
      if (/base ?board/i.test(t)) return 'Base board molding';
      if (/base molding/i.test(t)) return 'Base molding';
      if (/scribe/i.test(t)) return 'Scribe molding';
      return 'Trim';
  }
}

/** fetch with one retry when Shopify rate-limits (429), so a busy moment does not drop a card. */
async function fetchRetry(url) {
  const r = await fetch(url, { credentials: 'same-origin' });
  if (r.status !== 429) return r;
  await new Promise((res) => setTimeout(res, 800));
  return fetch(url, { credentials: 'same-origin' });
}
const cache = new Map();
function getProduct(handle) {
  if (!cache.has(handle)) {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    cache.set(
      handle,
      fetchRetry(`${root}products/${encodeURIComponent(handle)}.js`)
        .then((r) => (r && r.ok ? r.json() : null))
        .catch(() => null)
    );
  }
  return cache.get(handle);
}

async function resolveSlot(pat, suffixes) {
  for (const s of uniq(suffixes).slice(0, 6)) {
    const handle = pat.replace('{}', s);
    const p = await getProduct(handle);
    if (!p || !p.available) continue;
    if (!String(p.type || '').startsWith('Panel')) continue;
    const v = (p.variants || []).find((x) => x.available);
    if (!v) continue;
    return { p, v, handle };
  }
  return null;
}

/* Fallback (v3): when a finish's trim does not follow the handle pattern, read the finish collection's available Panel products
 * from templates/collection.dcd-trim.liquid and match each missing slot by SKU code, then by title. Only fetched when a core slot
 * (filler / toe kick) is missing or the row would have 0-1 cards. */
const listCache = new Map();
function collectionTrim(coll) {
  if (!coll) return Promise.resolve([]);
  if (!listCache.has(coll)) {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const get = (n) =>
      fetchRetry(`${root}collections/${encodeURIComponent(coll)}?view=dcd-trim&page=${n}`)
        .then((r) => (r && r.ok ? r.json() : null))
        .catch(() => null);
    listCache.set(
      coll,
      (async () => {
        const first = await get(1);
        if (!first || !Array.isArray(first.items)) return [];
        let items = first.items;
        const pages = Math.min(Number(first.pages) || 1, 12);
        if (pages > 1) {
          const rest = await Promise.all(Array.from({ length: pages - 1 }, (_, i) => get(i + 2)));
          for (const r of rest) if (r && Array.isArray(r.items)) items = items.concat(r.items);
        }
        return items;
      })()
    );
  }
  return listCache.get(coll);
}

const CODE = (re) => new RegExp(`(^|[\\s:])(${re})(\\s|$)`, 'i');
const SKIP = /10\s?FT|\b120\b|\b94\b|-94\b/i;
/** Tests in priority order for a slot: {s: regex on the variant SKU} or {t: regex on the product title}. */
function fallbackTests(slot, c, v) {
  const hs = hList(c.h, [30, 36, 39, 42]);
  switch (slot) {
    case 'toe':
      return [{ s: CODE('TK8(-96)?(-RTA)?') }, { s: CODE('TK8-3/4(-96)?') }, { s: CODE('TK96B?') }, { s: CODE('TKS[.0-9]*') }, { t: /toe (kick|board)(?!.*cap)/i }];
    case 'bf':
      if (v === 'Eurocraft') return [{ s: CODE('WF336') }];
      if (v === 'KCD' || v === 'CNC Cabinetry') return [{ s: CODE('F342') }];
      return [{ s: CODE('BF3') }, { t: /\bbase filler\b/i }];
    case 'wf':
      return hs.slice(0, 2).map((h) => ({ s: CODE(`W?F3${h}(-RTA)?`) }));
    case 'cf':
      return hs.slice(0, 1).map((h) => ({ s: CODE(`CF3${h}`) }));
    case 'tf':
      return [{ s: CODE('TF396(-96)?') }, { s: CODE('W?F396(-RTA)?') }, { s: CODE('OLF396') }];
    case 'crown':
      return [{ s: CODE('CM8-S|CM-9') }, { t: /shaker crown/i }, { s: CODE('A?CM8|CCM8|CM-1') }, { t: /crown molding/i }];
    case 'lr':
      return [{ s: CODE('LRM|TLR8-[12]|LTRM[123]') }, { t: /light rail/i }];
    case 'dw':
      return [{ s: CODE('DWR3|DWP(-RTA)?') }, { t: /dish ?washer/i }];
    case 'trim1':
      return [{ s: CODE('SM8-S(-96)?|SM8(-RTA)?|SCM') }, { t: /simple scribe|scribe molding/i }];
    case 'trim2':
      return [{ s: CODE('QRM8?(-96)?|BBM|BM8(-RTA)?|SHM') }, { t: /shoe molding|quarter round|base ?board/i }];
    case 'end':
      if (c.k === 'Base') return [{ s: CODE('BEP|BFP') }, { t: /base end panel|base finished end/i }];
      if (c.k === 'Wall') return hs.slice(0, 1).map((h) => ({ s: CODE(`WEP${h}|D12${h}(-RTA)?|WDD13${h}`) })).concat([{ t: /wall end panel/i }]);
      if (c.k === 'Tall') return [{ s: CODE('REP2496|REP2596|TEP3096|FP2496|PNL2596|UREP2[45]') }, { t: /tall end panel/i }];
      if (c.k === 'Vanity') return [{ s: CODE('VBEP|VFP|VDD2235') }, { t: /vanity.*end panel/i }, { s: CODE('BEP') }];
      return [];
    default:
      return [];
  }
}
function matchFallback(items, slot, c, v) {
  for (const test of fallbackTests(slot, c, v)) {
    for (const it of items) {
      if (!String(it.ty || '').startsWith('Panel') || SKIP.test(it.t)) continue;
      if (/cap\b/i.test(it.t) && slot === 'toe') continue;
      for (const vr of it.v || []) {
        if (SKIP.test(vr.s || '') || SKIP.test(vr.vt || '')) continue;
        const hit = test.s ? test.s.test(vr.s || '') : test.t.test(it.t);
        if (!hit) continue;
        const vt = String(vr.vt || '').split(' / ').filter((x) => x && x !== 'Default Title').join(' / ');
        const multi = (it.v || []).length > 1 && vt;
        return {
          handle: it.h,
          fb: true,
          p: { title: multi ? `${it.t} (${vt})` : it.t, featured_image: it.img || '' },
          v: { id: vr.id, price: vr.p },
        };
      }
    }
  }
  return null;
}
const CORE = { Base: ['bf', 'toe'], Wall: ['wf'], Tall: ['toe'], Vanity: ['toe'] };

function money(cents) {
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function img(src, w) {
  if (!src) return '';
  const u = src.startsWith('//') ? 'https:' + src : src;
  return u + (u.includes('?') ? '&' : '?') + 'width=' + w;
}
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** Build the groups: product mode = 1 group; cart mode = up to 2 finishes, largest cabinet quantity first. */
function groupsFrom(cfg) {
  const by = new Map();
  for (const it of cfg.items || []) {
    if (!RULES[it.v]) continue;
    const g = by.get(it.pat) || { v: it.v, pat: it.pat, name: it.name, c: it.c || '', q: 0, kinds: {}, hs: {}, t: it.t };
    g.q += it.q || 1;
    g.kinds[it.k] = (g.kinds[it.k] || 0) + (it.q || 1);
    if (it.k === 'Wall') {
      const h = wallHeight(it.t);
      if (h >= 30) g.hs[h] = (g.hs[h] || 0) + (it.q || 1);
    }
    if (it.k === 'Wall' && !g.wt) g.wt = it.t;
    by.set(it.pat, g);
  }
  return [...by.values()].sort((a, b) => b.q - a.q).slice(0, cfg.mode === 'cart' ? 2 : 1);
}

/** Slots to show for a group, in order: [{slot, k, sufs}]. Product: the page's cabinet type. Cart: every type in the
 * cart for that finish (Base, Wall, Tall, Vanity order); repeats are dropped after lookup (same handle). */
function slotsFor(g, mode) {
  const R = RULES[g.v];
  const topH = Number(Object.entries(g.hs).sort((a, b) => b[1] - a[1])[0]?.[0] || 0);
  const kinds = mode === 'product' ? Object.keys(g.kinds).slice(0, 1) : ['Base', 'Wall', 'Tall', 'Vanity'].filter((x) => g.kinds[x]);
  const out = [];
  for (const k of kinds) {
    const ctx = { k, h: mode === 'product' ? wallHeight(g.t) : topH, pat: g.pat, name: g.name, t: mode === 'product' ? g.t : k === 'Wall' ? g.wt || '' : '' };
    for (const slot of ORDER[k] || []) {
      const sufs = R[slot] ? R[slot](ctx) : [];
      if (!sufs.length) continue;
      if (out.some((o) => o.slot === slot && o.sufs.join() === sufs.join())) continue;
      out.push({ slot, k, sufs });
    }
  }
  return out;
}

class DcdFinishRun extends HTMLElement {
  static observedAttributes = ['data-cfg'];
  #started = false;
  #io = null;
  #mo = null;
  #run = 0;

  connectedCallback() {
    if (this.#io || this.#mo) return;
    // In the cart drawer, load when the drawer opens (its scroll area clips an IntersectionObserver).
    const dialog = this.closest('dialog');
    if (dialog) {
      const start = () => {
        if (dialog.open && !this.#started) {
          this.#started = true;
          this.render();
        }
      };
      this.#mo = new MutationObserver(start);
      this.#mo.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      start();
      return;
    }
    this.#io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.#io.disconnect();
          this.#started = true;
          this.render();
        }
      },
      { rootMargin: '900px 0px' }
    );
    this.#io.observe(this);
  }

  disconnectedCallback() {
    this.#io?.disconnect();
    this.#io = null;
    this.#mo?.disconnect();
    this.#mo = null;
  }

  attributeChangedCallback(name, oldV, newV) {
    if (oldV !== null && oldV !== newV && this.#started) this.render();
  }

  get cfg() {
    try {
      return JSON.parse(this.dataset.cfg || '{}');
    } catch (e) {
      return {};
    }
  }

  async render() {
    const run = ++this.#run;
    const cfg = this.cfg;
    const mode = cfg.mode || 'product';
    const inCart = new Set(cfg.incart || []);
    const t0 = performance.now();
    const groups = groupsFrom(cfg);
    const built = await Promise.all(
      groups.map(async (g) => {
        const slots = slotsFor(g, mode);
        const res = await Promise.all(slots.map((s) => resolveSlot(g.pat, s.sufs).then((r) => (r ? { ...r, slot: s.slot, k: s.k } : null))));
        const found = res.filter(Boolean).length;
        const coreMiss = slots.some((s, i) => !res[i] && (CORE[s.k] || []).includes(s.slot));
        if (found <= 1 || coreMiss) {
          const coll = g.c || g.pat.replace(/-?\{\}-?/, '');
          const items = await collectionTrim(coll);
          if (items.length) {
            slots.forEach((s, i) => {
              if (res[i]) return;
              const ctx = { k: s.k, h: mode === 'product' ? wallHeight(g.t) : Number(Object.entries(g.hs).sort((a, b) => b[1] - a[1])[0]?.[0] || 0), pat: g.pat, name: g.name, t: g.t };
              const m = matchFallback(items, s.slot, ctx, g.v);
              if (m) res[i] = { ...m, slot: s.slot, k: s.k };
            });
          }
        }
        const seen = new Set();
        const cards = res.filter((r) => r && !seen.has(r.handle + '#' + r.v.id) && seen.add(r.handle + '#' + r.v.id)).slice(0, MAX_CARDS[mode] || 5);
        for (const c of cards) c.label = labelFor(c.slot, c.k, g.v, c.p.title);
        return { g, cards };
      })
    );
    if (run !== this.#run) return;
    const wrap = this.querySelector('.dcd-fr__wrap');
    const box = this.querySelector('.dcd-fr__groups');
    const sub = this.querySelector('.dcd-fr__sub');
    if (!wrap || !box) return;
    const shown = built.filter((b) => b.cards.length);
    if (!shown.length) {
      wrap.hidden = true;
      this.dataset.ms = String(Math.round(performance.now() - t0));
      this.dataset.cards = '0';
      return;
    }
    if (mode === 'product') {
      const kw = { Base: 'base cabinet', Wall: 'wall cabinet', Tall: 'tall cabinet', Vanity: 'vanity' }[Object.keys(shown[0].g.kinds)[0]] || 'cabinet';
      sub.textContent = `Matching ${kw} trim in ${shown[0].g.name || 'this finish'}.`;
    } else {
      sub.textContent = shown.length > 1 ? 'Matching trim for the cabinets in your cart.' : `Matching trim in ${shown[0].g.name || 'your finish'} for the cabinets in your cart.`;
    }
    box.innerHTML = shown
      .map(
        (b) =>
          `<div class="dcd-fr__grp">${shown.length > 1 ? `<p class="dcd-fr__gname">${esc(b.g.name || '')}</p>` : ''}<ul class="dcd-fr__grid" role="list">${b.cards
            .map((c) => {
              const url = `/products/${c.handle}` + (c.fb ? `?variant=${c.v.id}` : '');
              const im = c.p.featured_image ? `<img class="dcd-fr__img" src="${esc(img(c.p.featured_image, 240))}" alt="${esc(c.p.title)}" width="240" height="240" loading="lazy">` : '<span class="dcd-fr__img" aria-hidden="true"></span>';
              let nm = b.g.name && c.p.title.startsWith(b.g.name + ' ') ? c.p.title.slice(b.g.name.length + 1) : c.p.title;
              if (nm === c.p.title && b.g.v === 'Eurocraft' && nm.includes(' - ')) nm = nm.slice(nm.indexOf(' - ') + 3);
              const inc = inCart.has(c.handle) ? '<span class="dcd-fr__in">In your cart</span>' : '';
              return `<li><div class="dcd-fr__card"><span class="dcd-fr__lab">${esc(c.label)}</span><a href="${esc(url)}" tabindex="-1" aria-hidden="true">${im}</a><a class="dcd-fr__name" href="${esc(url)}" title="${esc(c.p.title)}">${esc(nm)}</a>${inc}<div class="dcd-fr__row"><span class="dcd-fr__price">${money(c.v.price)}</span><button type="button" class="dcd-fr__btn" data-variant="${c.v.id}" aria-label="Add ${esc(c.p.title)} to cart">Add to cart</button></div><span class="dcd-fr__err" role="status" hidden></span></div></li>`;
            })
            .join('')}</ul></div>`
      )
      .join('');
    wrap.hidden = false;
    box.querySelectorAll('.dcd-fr__btn').forEach((btn) => btn.addEventListener('click', (e) => this.add(e.currentTarget)));
    this.dataset.ms = String(Math.round(performance.now() - t0));
    this.dataset.cards = String(shown.reduce((n, b) => n + b.cards.length, 0));
  }

  async add(btn) {
    const id = Number(btn.dataset.variant);
    const err = btn.closest('.dcd-fr__card')?.querySelector('.dcd-fr__err');
    btn.disabled = true;
    const label = btn.textContent;
    btn.textContent = 'Adding';
    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const r = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const data = await r.json();
      if (!r.ok || data.status) throw new Error(data.description || data.message || 'Could not add this item');
      const cart = await fetch(`${root}cart.js`).then((x) => x.json()).catch(() => null);
      btn.textContent = 'Added';
      btn.classList.add('is-added');
      if (err) err.hidden = true;
      // Same event the theme's own add-to-cart sends: updates the cart bubble and re-renders the cart drawer / page.
      const ev = new Event('cart:update', { bubbles: true });
      ev.detail = {
        resource: cart || {},
        sourceId: 'dcd-finish-run',
        data: { source: 'dcd-finish-run', itemCount: cart ? cart.item_count : 0, variantId: String(id), sections: data.sections },
      };
      document.dispatchEvent(ev);
      setTimeout(() => {
        btn.textContent = label;
        btn.classList.remove('is-added');
        btn.disabled = false;
      }, 2500);
    } catch (e) {
      btn.textContent = label;
      btn.disabled = false;
      if (err) {
        err.textContent = String(e.message || e);
        err.hidden = false;
      }
    }
  }
}

if (!customElements.get('dcd-finish-run')) customElements.define('dcd-finish-run', DcdFinishRun);
